import React, { useEffect, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import {
  BriefcaseBusiness,
  ChevronLeft,
  KeyRound,
  LogOut,
  MailCheck,
  Route,
  Sparkles,
  User,
} from 'lucide-react';
import logoImg from 'src/assets/images/logo.png';
import { useAuthActions } from 'src/hooks/useAuthActions';
import {
  getUser,
  isAuthenticated as checkIsAuthenticated,
} from 'src/utils/authStorage';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from 'src/components/ui/dropdown-menu';
import {
  Sidebar,
  SidebarContent,
  SidebarFooter,
  SidebarGroup,
  SidebarHeader,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarRail,
  useSidebar,
} from 'src/components/ui/sidebar';

const sidebarStyles = `
  @keyframes slideInLeft {
    from { opacity: 0; transform: translateX(-8px); }
    to   { opacity: 1; transform: translateX(0); }
  }

  .sidebar-nav-item {
    animation: slideInLeft 0.22s ease both;
    margin-bottom: 1px;
  }
  .sidebar-nav-item:nth-child(1) { animation-delay: 0.05s; }
  .sidebar-nav-item:nth-child(2) { animation-delay: 0.12s; }
  .sidebar-nav-item:nth-child(3) { animation-delay: 0.19s; }

  .sidebar-nav-btn {
    display: flex;
    align-items: center;
    gap: 11px;
    width: 100%;
    padding: 9px 12px;
    border-radius: 8px;
    font-size: 0.9rem;
    font-weight: 500;
    letter-spacing: -0.005em;
    cursor: pointer;
    border: none;
    background: transparent;
    transition: background 0.15s ease, color 0.15s ease;
    position: relative;
    text-decoration: none;
    color: rgba(255, 255, 255, 0.82);
    white-space: nowrap;
  }

  .sidebar-nav-btn.collapsed {
    padding: 10px 14px;
    justify-content: center !important;
    gap: 0;
  }

  .sidebar-nav-btn.inactive:hover {
    background: rgba(255, 255, 255, 0.08);
    color: rgba(255, 255, 255, 0.96);
  }

  .sidebar-nav-btn.active {
    background: rgba(255, 255, 255, 0.16);
    color: #fff;
    font-weight: 500;
  }

  .sidebar-nav-btn.active::before {
    content: '';
    position: absolute;
    left: 0;
    top: 50%;
    transform: translateY(-50%);
    width: 3px;
    height: 18px;
    border-radius: 0 3px 3px 0;
    background: #fff;
  }

  .sidebar-nav-btn.active.collapsed::before {
    display: none;
  }

  .nav-icon {
    width: 20px;
    height: 20px;
    min-width: 20px;
    display: flex;
    align-items: center;
    justify-content: center;
    opacity: 0.75;
    flex-shrink: 0;
  }

  .sidebar-nav-btn.active .nav-icon,
  .sidebar-nav-btn:hover .nav-icon {
    opacity: 1;
  }

  /* Collapsed nav buttons: shadcn forces !size-8 / !p-2 on the icon rail which
     clips our 20px icon. Override (higher-specificity + !important, and this
     <style> sits after Tailwind in the DOM so it wins the tie) so the icon
     stays centered and uncropped in the 64px rail. */
  [data-collapsible='icon'] .sidebar-nav-btn.collapsed {
    width: 40px !important;
    height: 40px !important;
    padding: 0 !important;
    margin: 0 auto !important;
    justify-content: center !important;
  }
  [data-collapsible='icon'] .sidebar-nav-btn.collapsed .nav-icon {
    width: 18px;
    height: 18px;
    min-width: 18px;
  }
  /* Footer profile trigger: same shadcn !size-8 override would crop the 34px
     avatar — restore a centered, full-width tappable tile when collapsed. */
  /* Double class (.ah-sb-profile-btn.ah-sb-profile-btn) raises specificity to
     match shadcn's group-data-[collapsible=icon]:!p-2 / !size-8 (0,3,0); this
     rule is later in the cascade so it wins the tie. */
  [data-collapsible='icon'] .ah-sb-profile-btn.ah-sb-profile-btn {
    width: 100% !important;
    height: auto !important;
    padding: 15px !important;
    justify-content: center !important;
  }

  /* Edge-mounted collapse/expand toggle (mirrors positions.html .sb-collapse-btn) */
  .ah-sb-collapse {
    position: absolute;
    top: 18px;
    right: -12px;
    z-index: 30;
    width: 24px;
    height: 24px;
    min-width: 24px;
    border-radius: 50%;
    background: var(--surface);
    border: 1px solid var(--border);
    color: var(--text-muted);
    align-items: center;
    justify-content: center;
    cursor: pointer;
    box-shadow: var(--shadow-sm);
    opacity: 0;
    transition: opacity 0.3s ease, color 0.15s ease, background 0.15s ease,
      border-color 0.15s ease, box-shadow 0.15s ease;
  }
  [data-sidebar='sidebar']:hover .ah-sb-collapse,
  [data-state='collapsed'] .ah-sb-collapse,
  .ah-sb-collapse:focus-visible {
    opacity: 1;
  }
  .ah-sb-collapse svg {
    width: 13px;
    height: 13px;
    stroke-width: 2;
    transition: transform 0.28s cubic-bezier(0.4, 0, 0.2, 1);
  }
  [data-state='collapsed'] .ah-sb-collapse svg {
    transform: rotate(180deg);
  }
  .ah-sb-collapse:hover {
    background: var(--primary-light);
    color: var(--primary);
    border-color: rgba(68, 114, 196, 0.22);
    box-shadow: 0 2px 6px rgba(68, 114, 196, 0.16);
  }
  [data-state='collapsed'] .ah-sb-collapse {
    background: var(--primary-light);
    color: var(--primary);
    border-color: rgba(68, 114, 196, 0.2);
  }
  [data-state='collapsed'] .ah-sb-collapse:hover {
    background: var(--primary);
    color: #fff;
    border-color: var(--primary);
    box-shadow: 0 2px 8px rgba(68, 114, 196, 0.28);
  }
  .ah-sb-collapse:focus-visible {
    outline: 2px solid var(--ring);
    outline-offset: 2px;
  }
`;

const defaultMenuItems = [
  {
    title: 'My Profile',
    path: '/profile-dashboard',
    icon: User,
  },
  {
    title: 'My Skills',
    path: '/my-skills',
    icon: Sparkles,
  },
  {
    title: 'My Applications',
    path: '/my-applications',
    icon: Route,
  },
  {
    title: 'Open Positions',
    path: '/openings',
    icon: BriefcaseBusiness,
  },
];

const isRouteActive = (pathname, routePath) =>
  pathname === routePath || pathname.startsWith(`${routePath}/`);

const AppSidebar = () => {
  const [userData, setUserData] = useState(null);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const location = useLocation();
  const { open, isMobile, openMobile, setOpenMobile, toggleSidebar } =
    useSidebar();
  const { logout } = useAuthActions();

  useEffect(() => {
    const checkAuth = () => {
      // Same-tab localStorage writes don't fire the 'storage' event; read directly on navigation
      const user = getUser();
      const authenticated = checkIsAuthenticated();

      setUserData(user);
      setIsAuthenticated(authenticated);
    };

    checkAuth();
    // Auth lives in localStorage (shared across tabs). Re-check when the route changes.
  }, [location.pathname]);

  const handleLogout = async () => {
    try {
      // If the mobile sidebar Sheet is open, close it before navigation.
      // Radix Dialog/Sheet can temporarily set body pointer-events/overflow; closing first avoids stale state.
      if (isMobile && openMobile) {
        console.debug('[logout] closing mobile sidebar sheet before logout');
        setOpenMobile(false);
        // Let Radix flush close side-effects before route change.
        await new Promise((r) => setTimeout(r, 0));
      }

      console.debug('[logout] triggered', {
        pathname: location.pathname,
        isMobile,
        openMobile,
        bodyPointerEvents: document?.body?.style?.pointerEvents,
        bodyOverflow: document?.body?.style?.overflow,
      });

      await logout();
    } catch (e) {
      console.error('[logout] failed', e);
    }
  };
  const shouldShowVerifyEmailMenu = userData?.secAuth === 'none';
  const shouldShowChangePasswordMenu = userData?.secAuth === 'email_password';
  const menuItems = defaultMenuItems;

  const closeMobileSidebar = () => {
    if (isMobile) {
      setOpenMobile(false);
    }
  };

  return (
    <>
      <style>{sidebarStyles}</style>
      <Sidebar collapsible="icon" className="!border-r-0 !bg-sidebar-gradient">
        {/* Edge-mounted collapse/expand toggle — desktop only (mobile uses the header trigger) */}
        <button
          type="button"
          onClick={toggleSidebar}
          aria-label={open ? 'Collapse sidebar' : 'Expand sidebar'}
          aria-expanded={open}
          className="ah-sb-collapse hidden md:flex"
        >
          <ChevronLeft aria-hidden="true" />
        </button>
        <SidebarHeader className="p-0 border-b border-white/10">
          <a
            href="/dashboard"
            onClick={closeMobileSidebar}
            className={`flex items-center gap-[11px] h-[3.4rem] ${
              !open ? 'justify-center p-4' : 'px-[18px]'
            }`}
          >
            <img
              src={logoImg}
              alt="AHEDGE Logo"
              className="h-6 w-auto flex-shrink-0"
            />
            {open && (
              <span className="text-[0.96rem] font-semibold text-white tracking-[-0.015em] whitespace-nowrap">
                AHEDGE
              </span>
            )}
          </a>
        </SidebarHeader>
        <SidebarContent className={`${!open ? 'p-0' : 'p-0'}`}>
          <SidebarGroup className={`${!open ? 'p-0 pt-2' : 'p-2'}`}>
            <SidebarMenu className={`${!open ? 'px-[5px]' : 'p-[2px]'}`}>
              {menuItems.map((item) => {
                const isActive = isRouteActive(location.pathname, item.path);

                return (
                  <SidebarMenuItem key={item.path} className="sidebar-nav-item">
                    <SidebarMenuButton
                      asChild
                      isActive={isActive}
                      tooltip={!open ? item.title : undefined}
                      className="h-auto"
                    >
                      <Link
                        to={item.path}
                        onClick={closeMobileSidebar}
                        className={`sidebar-nav-btn ${isActive ? 'active' : 'inactive'} ${!open ? 'collapsed' : ''}`}
                      >
                        <span className="nav-icon">
                          <item.icon size={18} strokeWidth={1.7} />
                        </span>
                        {open && <span>{item.title}</span>}
                        {item.isAttention && open && (
                          <span className="ml-auto rounded-full bg-white/20 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-white/60">
                            Required
                          </span>
                        )}
                      </Link>
                    </SidebarMenuButton>
                  </SidebarMenuItem>
                );
              })}
            </SidebarMenu>
          </SidebarGroup>
        </SidebarContent>
        {isAuthenticated && userData && (
          <SidebarFooter className="relative z-30 bg-transparent border-t border-white/[0.08] p-0">
            <div>
              <DropdownMenu modal={false}>
                <DropdownMenuTrigger asChild>
                  <SidebarMenuButton
                    data-testid="user-menu-trigger"
                    className={`ah-sb-profile-btn w-full h-auto rounded-none bg-transparent text-white transition-colors hover:bg-white/[0.05] hover:text-white active:bg-white/[0.05] active:text-white data-[state=open]:bg-transparent data-[state=open]:text-white data-[state=open]:hover:bg-white/[0.05] data-[state=open]:hover:text-white ${
                      !open
                        ? 'p-[15px] justify-center'
                        : 'px-[14px] pt-[14px] pb-[16px]'
                    }`}
                  >
                    <div
                      className="flex-shrink-0 w-[34px] h-[34px] rounded-full flex items-center justify-center font-semibold text-[0.78rem] text-white"
                      style={{
                        background:
                          'linear-gradient(135deg, rgba(255,255,255,0.22), rgba(255,255,255,0.08))',
                        border: '1.5px solid rgba(255,255,255,0.22)',
                      }}
                    >
                      {(userData?.firstName?.[0] || 'U').toUpperCase()}
                    </div>
                    {open && (
                      <div className="flex-1 min-w-0 overflow-hidden">
                        <div className="text-[0.9rem] font-semibold text-white truncate leading-[1.3] tracking-[-0.005em]">
                          {userData?.firstName || ''} {userData?.lastName || ''}
                        </div>
                        {userData?.email && userData?.emailVerified && (
                          <div
                            className="text-[0.7rem] font-normal truncate leading-[1.3] tracking-[-0.005em]"
                            style={{ color: 'rgba(255,255,255,0.6)' }}
                          >
                            {userData.email}
                          </div>
                        )}
                      </div>
                    )}
                  </SidebarMenuButton>
                </DropdownMenuTrigger>
                <DropdownMenuContent
                  side={open ? 'top' : 'right'}
                  align="start"
                  alignOffset={open ? 8 : 0}
                  sideOffset={8}
                  className={`z-[100001] rounded-[10px] border border-[var(--border-light)] bg-[var(--surface)] p-1.5 shadow-lg ${!open ? 'w-[220px]' : ''}`}
                  style={
                    open
                      ? {
                          width:
                            'calc(var(--radix-popper-anchor-width) - 16px)',
                        }
                      : undefined
                  }
                >
                  <DropdownMenuLabel className="flex items-center gap-3 px-3.5 py-3 border-b border-gray-100 dark:border-slate-700 mb-1">
                    <div
                      className="h-9 w-9 flex-shrink-0 rounded-full flex items-center justify-center font-bold text-sm"
                      style={{
                        background: 'var(--ah-brand-primary-alt)',
                        color: '#fff',
                      }}
                    >
                      {(userData?.firstName?.[0] || 'U').toUpperCase()}
                    </div>
                    <div className="flex min-w-0 flex-col">
                      <span className="truncate text-[0.9rem] font-semibold text-gray-900 dark:text-slate-100">
                        {`${userData?.firstName || ''} ${userData?.lastName || ''}`.trim()}
                      </span>
                      {userData?.email && userData.emailVerified && (
                        <span className="truncate text-[0.72rem] text-gray-400 dark:text-slate-400">
                          {userData.email}
                        </span>
                      )}
                    </div>
                  </DropdownMenuLabel>
                  {shouldShowVerifyEmailMenu && (
                    <DropdownMenuItem
                      asChild
                      className="rounded-[6px] px-3 py-2 text-[0.78rem] font-medium text-gray-600 hover:bg-blue-50 hover:text-blue-700 dark:text-slate-300 dark:hover:bg-slate-700 dark:hover:text-slate-100 cursor-pointer"
                    >
                      <Link
                        to="/create-link"
                        onClick={closeMobileSidebar}
                        className="flex w-full items-center gap-2.5"
                      >
                        <MailCheck className="h-4 w-4 flex-shrink-0 text-gray-400" />
                        <span>Link Email</span>
                        <span className="ml-auto rounded-full bg-blue-50 px-2 py-0.5 text-[8px] font-semibold uppercase tracking-wide text-blue-500">
                          Required
                        </span>
                      </Link>
                    </DropdownMenuItem>
                  )}
                  {shouldShowChangePasswordMenu && (
                    <DropdownMenuItem
                      asChild
                      className="rounded-[6px] px-3 py-2 text-[0.78rem] font-medium text-gray-600 hover:bg-blue-50 hover:text-blue-700 dark:text-slate-300 dark:hover:bg-slate-700 dark:hover:text-slate-100 cursor-pointer"
                    >
                      <Link
                        to="/change-password"
                        onClick={closeMobileSidebar}
                        className="flex items-center gap-2.5"
                      >
                        <KeyRound className="h-4 w-4 flex-shrink-0 text-gray-400" />
                        <span>Change Password</span>
                      </Link>
                    </DropdownMenuItem>
                  )}
                  <DropdownMenuSeparator className="my-1 bg-gray-100 dark:bg-slate-700" />
                  <DropdownMenuItem
                    className="rounded-[6px] px-3 py-2 text-[0.78rem] font-medium text-red-500 hover:bg-red-50 hover:text-red-600 dark:text-red-400 dark:hover:bg-red-950/40 dark:hover:text-red-400 cursor-pointer"
                    onClick={handleLogout}
                  >
                    <LogOut className="mr-2.5 h-4 w-4 flex-shrink-0" />
                    <span>Sign Out</span>
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
          </SidebarFooter>
        )}
        <SidebarRail />
      </Sidebar>
    </>
  );
};

export default AppSidebar;
